import type { Entity, Me, Perm } from '../api/types.ts';

/** Everything an Owner can do. "Records own purchases" (wallet) is not in it: it is switched on
 * per role and gives that person a wallet. */
export const ALL_PERMS: Perm[] = ['view', 'export', 'review', 'edit', 'delete', 'audit', 'cash', 'categories', 'users', 'dues'];
/** The Super Admin: an owner's areas plus Settings → Companies everywhere. */
export const SUPER_PERMS: Perm[] = [...ALL_PERMS, 'companies'];

/** Opens Settings → Companies: the Super Admin, or a role with "Manage companies" somewhere. */
export function managesCompanies(me: Me): boolean {
  return me.isSuperAdmin || me.memberships.some((m) => (m.permissions ?? []).includes('companies'));
}

type Membership = Me['memberships'][number];

/** Uses the owner app there: an Owner, or a role with any permission besides "wallet" (an
 * office boy too, when an Owner gave the Office boy role owner-app areas). */
const isStaff = (m: Membership) =>
  m.role === 'admin' || (m.permissions === undefined ? m.role !== 'spender' : m.permissions.some((p) => p !== 'wallet'));

/** Spends from a wallet there: an office boy, or a role with "Records own purchases". */
const spends = (m: Membership) => m.role === 'spender' || (m.permissions ?? []).includes('wallet');

/** What each owner-app page needs in the place open now ("superAdmin": Super Admins only).
 * The menu and the page guard both use it, so a page shows only to roles given it there (owner's
 * request, 13 Oct 2026: "Manage companies" in one place no longer shows Portfolios in the others). */
export const PAGE_NEEDS: Record<string, Perm | 'superAdmin'> = {
  '/admin': 'view',
  '/admin/review': 'review',
  '/admin/cash': 'cash',
  '/admin/wallets': 'cash',
  '/admin/bills': 'cash',
  '/admin/transactions': 'view',
  '/admin/spending': 'view',
  '/admin/items': 'view',
  '/admin/dues': 'dues',
  '/admin/history': 'audit',
  '/admin/categories': 'categories',
  '/admin/users': 'users',
  '/admin/companies': 'companies',
  '/admin/salaries': 'cash',
  '/admin/workspaces': 'superAdmin',
  '/admin/super-admins': 'superAdmin',
};

/** May this person open the page in this place? Unknown paths: yes (the not-found page). */
export function canOpen(me: Me, entityId: string, path: string): boolean {
  const need = PAGE_NEEDS[path === '/admin/' ? '/admin' : path];
  if (need === undefined) return true;
  return need === 'superAdmin' ? me.isSuperAdmin : permsIn(me, entityId).has(need);
}

/** Entities this person works in through the owner app (all of them for the Super Admin). */
export function adminEntities(me: Me): Entity[] {
  if (me.isSuperAdmin) return me.entities;
  const staff = new Set(me.memberships.filter(isStaff).map((m) => m.entityId));
  return me.entities.filter((e) => staff.has(e.id));
}

/** Entities where this person records their own purchases from the phone app. */
export function spenderEntityIds(me: Me): Set<string> {
  // The server's list also covers Super Admins, whose role records own purchases by default.
  if (me.walletEntityIds) return new Set(me.walletEntityIds);
  return new Set(me.memberships.filter(spends).map((m) => m.entityId));
}

/** Gives cash from their own wallet to another spender there (phone app): only the place's Owner
 * or a Super Admin (owner's request, 22 Oct 2026); an office boy only adds what he bought. The
 * server checks the same. */
export function mayGiveCash(me: Me, entityId: string): boolean {
  return me.isSuperAdmin || me.memberships.some((m) => m.entityId === entityId && m.role === 'admin');
}

/** What the person may do in one entity (the Super Admin and Application Owners: everything). The server
 * checks the same on every request; this only decides what the screen offers. */
export function permsIn(me: Me, entityId: string): Set<Perm> {
  // Super Admins: the Super Admin role (only Super Admins change it), everything by default.
  if (me.isSuperAdmin) return new Set(me.superPermissions ?? SUPER_PERMS);
  const m = me.memberships.find((x) => x.entityId === entityId);
  if (!m) return new Set();
  // Owners: everything, unless an Owner re-tuned the Owner role in this place.
  if (m.role === 'admin' && m.permissions === undefined) return new Set(ALL_PERMS);
  return new Set(m.permissions ?? []);
}
